/* ==========================================================================
   Tire Size Comparison Calculator
   Self-contained. Loaded only on this calculator's page.
   No shared dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pc-tsc]');
  if (!root) return;

  var MM_PER_INCH = 25.4;
  var INCHES_PER_MILE = 63360;

  /* ====================================================================
     compute(input) -> result

     PURE. No DOM, no network, no globals. All calculator logic lives here
     so it can be reasoned about and tested in isolation.
     ==================================================================== */
  function spec(w, a, r) {
    var sidewall = w * (a / 100);              /* mm */
    var dia = (r * MM_PER_INCH) + (2 * sidewall);
    return {
      width: w,
      sidewallMm: sidewall,
      diaMm: dia,
      diaIn: dia / MM_PER_INCH,
      circIn: Math.PI * (dia / MM_PER_INCH),
      revsPerMile: INCHES_PER_MILE / (Math.PI * (dia / MM_PER_INCH))
    };
  }

  function compute(input) {
    var errors = [];
    ['w1','a1','r1','w2','a2','r2'].forEach(function (k) {
      if (!isFinite(input[k]) || input[k] <= 0) errors.push('Fill in both tire sizes.');
    });
    if (errors.length) return { ok: false, errors: [errors[0]] };

    var o = spec(input.w1, input.a1, input.r1);
    var n = spec(input.w2, input.a2, input.r2);

    var diaPct = ((n.diaIn - o.diaIn) / o.diaIn) * 100;
    var speed = isFinite(input.speed) && input.speed > 0 ? input.speed : 60;
    var trueSpeed = speed * (n.diaIn / o.diaIn);

    var status;
    var abs = Math.abs(diaPct);
    if (abs <= 1) {
      status = { key: 'good', label: num(diaPct, 1) + '% difference',
                 note: 'Practically identical. Speedometer error is negligible.' };
    } else if (abs <= 3) {
      status = { key: 'good', label: signed(diaPct, 1) + '% difference',
                 note: 'Inside the 3% rule most shops use. Safe to run on a stock vehicle.' };
    } else if (abs <= 5) {
      status = { key: 'soon', label: signed(diaPct, 1) + '% difference',
                 note: 'Past 3%. Check clearance at full lock and full compression, and expect a noticeable speedometer error.' };
    } else {
      status = { key: 'now', label: signed(diaPct, 1) + '% difference',
                 note: 'Well outside 3%. Expect rubbing, speedometer error and possible ABS or traction control complaints without a recalibration.' };
    }

    var warnings = [];
    if (Math.abs(n.width - o.width) >= 30) {
      warnings.push('That is a big jump in section width. Check that the new tire is rated for your rim width.');
    }

    return {
      ok: true, errors: [], warnings: warnings, status: status,
      figures: [
        { label: 'New overall diameter', value: num(n.diaIn, 2) + ' in',
          sub: 'Was ' + num(o.diaIn, 2) + ' in. ' + signed(n.diaIn - o.diaIn, 2) + ' in.' },
        { label: 'Speedometer at ' + num(speed, 0) + ' mph', value: num(trueSpeed, 1) + ' mph actual',
          sub: trueSpeed > speed
            ? 'You are going faster than the speedometer says.'
            : (trueSpeed < speed ? 'You are going slower than the speedometer says.'
                                 : 'No meaningful error.') }
      ],
      rows: [
        { head: 'Current ' + num(input.w1,0) + '/' + num(input.a1,0) + 'R' + num(input.r1,0) },
        { label: 'Sidewall height', value: num(o.sidewallMm, 1) + ' mm' },
        { label: 'Diameter', value: num(o.diaIn, 2) + ' in' },
        { label: 'Circumference', value: num(o.circIn, 1) + ' in' },
        { label: 'Revs per mile', value: num(o.revsPerMile, 0) },
        { head: 'New ' + num(input.w2,0) + '/' + num(input.a2,0) + 'R' + num(input.r2,0) },
        { label: 'Sidewall height', value: num(n.sidewallMm, 1) + ' mm' },
        { label: 'Diameter', value: num(n.diaIn, 2) + ' in' },
        { label: 'Circumference', value: num(n.circIn, 1) + ' in' },
        { label: 'Revs per mile', value: num(n.revsPerMile, 0) },
        { head: 'Change' },
        { label: 'Diameter', value: signed(diaPct, 2) + '%' },
        { label: 'Section width', value: signed(n.width - o.width, 0) + ' mm' },
        { label: 'Sidewall', value: signed(n.sidewallMm - o.sidewallMm, 1) + ' mm' },
        { label: 'Ground clearance', value: signed((n.diaIn - o.diaIn) / 2, 2) + ' in' },
        { label: 'Odometer over 100 mi', value: num(100 * (o.diaIn / n.diaIn), 1) + ' mi shown' }
      ]
    };
  }

  /* ====================================================================
     Formatting
     ==================================================================== */
  function comma(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function num(n, dp) {
    if (!isFinite(n)) return '-';
    var v = Number(n).toFixed(dp == null ? 2 : dp);
    if (dp !== 0 && v.indexOf('.') > -1) v = v.replace(/\.?0+$/, '');
    var parts = v.split('.');
    return comma(parts[0]) + (parts[1] ? '.' + parts[1] : '');
  }

  function signed(n, dp) {
    if (!isFinite(n)) return '-';
    return (n > 0 ? '+' : '') + num(n, dp);
  }

  /* ====================================================================
     DOM wiring
     ==================================================================== */
  var FIELDS = [
      {
          "name": "w1",
          "key": "w1",
          "type": "number"
      },
      {
          "name": "a1",
          "key": "a1",
          "type": "number"
      },
      {
          "name": "r1",
          "key": "r1",
          "type": "number"
      },
      {
          "name": "w2",
          "key": "w2",
          "type": "number"
      },
      {
          "name": "a2",
          "key": "a2",
          "type": "number"
      },
      {
          "name": "r2",
          "key": "r2",
          "type": "number"
      },
      {
          "name": "speed",
          "key": "s",
          "type": "number"
      }
  ];

  var els = {
    error:    root.querySelector('[data-r-error]'),
    status:   root.querySelector('[data-r-status]'),
    statusLbl:root.querySelector('[data-r-status-label]'),
    statusVal:root.querySelector('[data-r-status-value]'),
    statusNote:root.querySelector('[data-r-status-note]'),
    figures:  root.querySelector('[data-r-figures]'),
    rows:     root.querySelector('[data-r-rows]'),
    warnings: root.querySelector('[data-r-warnings]')
  };

  var STATUS_CLASSES = ['is-good','is-monitor','is-soon','is-now','is-critical'];

  function field(name) { return root.querySelector('[data-f="' + name + '"]'); }

  function readInput() {
    var o = {};
    FIELDS.forEach(function (f) {
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') o[f.name] = el.checked;
      else if (f.type === 'number') o[f.name] = parseFloat(el.value);
      else o[f.name] = el.value;
    });
    return o;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function renderFigures(figs) {
    els.figures.innerHTML = (figs || []).map(function (f) {
      return '<div class="pc-tsc__figure">'
        + '<div class="pc-tsc__figure-label">' + esc(f.label) + '</div>'
        + '<div class="pc-tsc__figure-value' + (f.muted ? ' is-muted' : '') + '">'
        + esc(f.value) + '</div>'
        + (f.sub ? '<div class="pc-tsc__figure-sub">' + esc(f.sub) + '</div>' : '')
        + '</div>';
    }).join('');
  }

  function renderRows(rows) {
    if (!els.rows) return;
    els.rows.innerHTML = (rows || []).map(function (r) {
      if (r.head) {
        return '<div class="pc-tsc__row pc-tsc__rows-head">' + esc(r.head) + '</div>';
      }
      return '<div class="pc-tsc__row">'
        + '<span class="pc-tsc__row-label">' + esc(r.label) + '</span>'
        + '<span class="pc-tsc__row-value">' + esc(r.value) + '</span>'
        + '</div>';
    }).join('');
  }

  function clearResult(message) {
    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      els.statusVal.textContent  = 'Waiting for your numbers';
      els.statusNote.textContent = message || '';
    }
    els.figures.innerHTML = '';
    if (els.rows) els.rows.innerHTML = '';
    if (els.warnings) els.warnings.style.display = 'none';
  }

  function render() {
    var input  = readInput();
    var result = compute(input);

    if (!result.ok) {
      els.error.textContent = result.errors[0];
      els.error.classList.add('is-shown');
      clearResult(result.errors[0]);
      return;
    }
    els.error.classList.remove('is-shown');

    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      if (result.status) {
        els.status.classList.add('is-' + result.status.key);
        els.statusVal.textContent  = result.status.label;
        els.statusNote.textContent = result.status.note || '';
      } else {
        els.statusVal.textContent  = '';
        els.statusNote.textContent = '';
      }
    }

    renderFigures(result.figures);
    renderRows(result.rows);

    if (els.warnings) {
      if (result.warnings && result.warnings.length) {
        els.warnings.textContent = result.warnings[0];
        els.warnings.style.display = '';
      } else {
        els.warnings.style.display = 'none';
      }
    }

    writePermalink(input);
  }

  /* ====================================================================
     Shareable result permalinks
     ==================================================================== */
  function writePermalink(input) {
    if (!window.history || !window.history.replaceState) return;
    var q = FIELDS.map(function (f) {
      var v = input[f.name];
      if (f.type === 'checkbox') return v ? f.key + '=1' : '';
      if (v == null || v !== v || v === '') return '';
      return f.key + '=' + encodeURIComponent(v);
    }).filter(Boolean).join('&');
    try {
      window.history.replaceState(null, '', window.location.pathname + (q ? '?' + q : ''));
    } catch (e) {}
  }

  function applyPermalink() {
    if (!window.URLSearchParams) return;
    var p = new URLSearchParams(window.location.search);
    var any = false;
    FIELDS.forEach(function (f) {
      if (!p.has(f.key)) return;
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') el.checked = p.get(f.key) === '1';
      else el.value = p.get(f.key);
      any = true;
    });
    return any;
  }

  /* ====================================================================
     Init
     ==================================================================== */
  ['input', 'change'].forEach(function (evt) {
    root.addEventListener(evt, function (e) {
      if (e.target.closest('[data-r-form]')) render();
    });
  });

  applyPermalink();
  render();

  /* Exposed for testing and debugging only. */
  window.PC_TSC = { compute: compute };
})();
